import { collection, getDocs, orderBy, query, Timestamp, where } from "firebase/firestore";
import { getBarsAndDrinks } from "./marketDataService";
import { db } from "@/config/firebase";
import type { BrandAnalytics } from "@/types";
import type { BrandCatalogEntry } from "@/types/brandCatalog.types";
import { buildBrandIndex, brandGroupKey } from "@/utils/brandResolver";
import {
  extractCityFromAddress,
  getDisplayBarName,
  getDisplayCity,
  getDrinkBrand,
  normalizeDrinkCategory,
  normalizeSearchValue,
  sortDrinkCategories,
} from "@/utils/drinkMetadata";

export const brandAnalyticsService = {
  getBrandAnalytics: async (forceReload = false): Promise<BrandAnalytics[]> => {
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const [analyticsSnapshot, [barsSnapshot, drinksSnapshot], filterEventsSnapshot, brandCatalogSnapshot] = await Promise.all([
      getDocs(collection(db, "brandAnalytics")),
      getBarsAndDrinks(forceReload),
      // Nur der laufende Monat wird ausgewertet (Index type+timestamp existiert).
      getDocs(
        query(
          collection(db, "analyticsEvents"),
          where("type", "==", "filterApplied"),
          where("timestamp", ">=", Timestamp.fromDate(startOfMonth)),
          orderBy("timestamp", "desc"),
        ),
      ),
      getDocs(collection(db, "brandCatalog")),
    ]);

    // Kanonischer Marken-Katalog: löst Freitext-Schreibvarianten auf eine Marke
    // auf ("Krombacher Pils" + "Krombacher Radler" → Krombacher). Leerer Katalog
    // = unverändertes Freitext-Verhalten (Fallback im Resolver).
    const brandIndex = buildBrandIndex(
      brandCatalogSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...(doc.data() as Omit<BrandCatalogEntry, "id">),
      })),
    );

    const activeBarIds = new Set(
      barsSnapshot.docs
        .filter((barDoc) => {
          const status = barDoc.data().status;
          return status === "active" || status === undefined;
        })
        .map((barDoc) => barDoc.id),
    );

    const barMetaById = new Map(
      barsSnapshot.docs
        .filter((barDoc) => activeBarIds.has(barDoc.id))
        .map((barDoc) => {
          const data = barDoc.data() as Record<string, unknown>;
          const location = data.location as { latitude?: number; longitude?: number } | undefined;
          const lat =
            typeof data.latitude === "number"
              ? data.latitude
              : typeof location?.latitude === "number"
              ? location.latitude
              : undefined;
          const lon =
            typeof data.longitude === "number"
              ? data.longitude
              : typeof location?.longitude === "number"
              ? location.longitude
              : undefined;
          return [
            barDoc.id,
            {
              name: getDisplayBarName(data.name),
              city: getDisplayCity(
                data.city,
                extractCityFromAddress(data.address),
                (data.address as { city?: string } | undefined)?.city,
              ),
              claimed: data.isClaimed === true,
              lat,
              lon,
            },
          ];
        }),
    );

    const detailsByBrand = new Map<
      string,
      {
        brand: string;
        categories: Set<string>;
        bars: Map<string, { barName: string; city: string; itemCount: number; claimed: boolean; lat?: number; lon?: number }>;
        cities: Map<string, { city: string; barIds: Set<string>; itemCount: number }>;
      }
    >();

    for (const drinkDoc of drinksSnapshot.docs) {
      const data = drinkDoc.data() as Record<string, unknown>;

      // Skip archived/rejected drinks
      if (data.archived === true) continue;
      // Skip pending user contributions (not yet approved by admin)
      if (data.isUserContributed === true && data.approved !== true) continue;

      const brand = getDrinkBrand(data);
      if (!brand) {
        continue;
      }

      const barId = drinkDoc.ref.parent.parent?.id;
      if (!barId || !activeBarIds.has(barId)) {
        continue;
      }

      const group = brandGroupKey(brand, brandIndex);
      const normalizedBrand = group.key;
      if (!detailsByBrand.has(normalizedBrand)) {
        detailsByBrand.set(normalizedBrand, {
          brand: group.display,
          categories: new Set<string>(),
          bars: new Map(),
          cities: new Map(),
        });
      }

      const brandEntry = detailsByBrand.get(normalizedBrand)!;
      const barMeta = barMetaById.get(barId);
      const barName = getDisplayBarName(data.barName, barMeta?.name, barId);
      const city = getDisplayCity(data.city, extractCityFromAddress(data.address), barMeta?.city);
      const category = normalizeDrinkCategory(data.category);

      brandEntry.categories.add(category);

      const existingBar = brandEntry.bars.get(barId);
      brandEntry.bars.set(barId, {
        barName,
        city,
        itemCount: (existingBar?.itemCount ?? 0) + 1,
        claimed: existingBar?.claimed ?? barMeta?.claimed ?? false,
        lat: existingBar?.lat ?? barMeta?.lat,
        lon: existingBar?.lon ?? barMeta?.lon,
      });

      const existingCity = brandEntry.cities.get(city);
      brandEntry.cities.set(city, {
        city,
        barIds: existingCity?.barIds ?? new Set<string>(),
        itemCount: (existingCity?.itemCount ?? 0) + 1,
      });
      brandEntry.cities.get(city)!.barIds.add(barId);
    }

    const analyticsByNormalizedBrand = new Map(
      analyticsSnapshot.docs.map((analyticsDoc) => {
        const data = analyticsDoc.data() as Partial<BrandAnalytics> &
          Record<string, unknown>;
        const brand = String(data.brand ?? "").trim();
        // Über den Resolver schlüsseln, damit vorberechnete Engagement-Docs
        // an dieselbe kanonische Marke andocken wie die Live-Drink-Struktur.
        const normalizedBrand = brandGroupKey(
          brand || String(data.normalizedBrand ?? analyticsDoc.id),
          brandIndex,
        ).key;

        return [normalizedBrand, { id: analyticsDoc.id, data, brand }];
      }),
    );

    const filterDemandByBrand = new Map<
      string,
      {
        brand: string;
        applyCount: number;
        uniqueUsers: Set<string>;
        multiSelectCount: number;
      }
    >();

    let trackedBrandFilterAppliesThisMonth = 0;

    for (const eventDoc of filterEventsSnapshot.docs) {
      const data = eventDoc.data() as Record<string, unknown>;
      const metadata = (data.metadata ?? {}) as Record<string, unknown>;
      if (String(metadata.filterType ?? "") !== "brands") {
        continue;
      }

      const rawTimestamp = data.timestamp as { toDate?: () => Date } | undefined;
      const timestamp = rawTimestamp?.toDate?.();
      if (!timestamp || timestamp < startOfMonth) {
        continue;
      }

      const rawBrandValues = Array.isArray(metadata.brandValues)
        ? metadata.brandValues
        : String(metadata.filterValue ?? "")
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean);

      const uniqueBrands = Array.from(
        new Map(
          rawBrandValues
            .map((value) => String(value ?? "").trim())
            .filter(Boolean)
            .map((brand) => {
              const group = brandGroupKey(brand, brandIndex);
              return [group.key, group.display] as [string, string];
            }),
        ).entries(),
      );

      if (uniqueBrands.length === 0) {
        continue;
      }

      trackedBrandFilterAppliesThisMonth += 1;
      const isMultiSelect = uniqueBrands.length > 1;
      const userId = String(data.userId ?? "").trim();

      for (const [normalizedBrand, brand] of uniqueBrands) {
        if (!filterDemandByBrand.has(normalizedBrand)) {
          filterDemandByBrand.set(normalizedBrand, {
            brand,
            applyCount: 0,
            uniqueUsers: new Set<string>(),
            multiSelectCount: 0,
          });
        }

        const brandDemand = filterDemandByBrand.get(normalizedBrand)!;
        brandDemand.applyCount += 1;
        if (userId) {
          brandDemand.uniqueUsers.add(userId);
        }
        if (isMultiSelect) {
          brandDemand.multiSelectCount += 1;
        }
      }
    }

    const allBrands = new Set<string>([
      ...analyticsByNormalizedBrand.keys(),
      ...detailsByBrand.keys(),
      ...filterDemandByBrand.keys(),
    ]);

    return Array.from(allBrands)
      .map((normalizedBrand) => {
        const analyticsEntry = analyticsByNormalizedBrand.get(normalizedBrand);
        const data = (analyticsEntry?.data ?? {}) as Partial<BrandAnalytics> &
          Record<string, unknown>;
        const brand = analyticsEntry?.brand ?? "";
        const details = detailsByBrand.get(normalizedBrand);
        const filterDemand = filterDemandByBrand.get(normalizedBrand);

        const normalizedCategories = sortDrinkCategories(
          Array.from(
            new Set([
              ...((data.categories as string[] | undefined) ?? []).map((category) =>
                normalizeDrinkCategory(category),
              ),
              ...(details ? Array.from(details.categories) : []),
            ]),
          ),
        );

        const topCities = details
          ? Array.from(details.cities.values())
              .map((city) => ({
                city: city.city,
                barCount: city.barIds.size,
                itemCount: city.itemCount,
              }))
              .sort((left, right) => {
                if (right.barCount !== left.barCount) {
                  return right.barCount - left.barCount;
                }
                return right.itemCount - left.itemCount;
              })
          : ((data.topCities as BrandAnalytics["topCities"] | undefined) ?? []).map(
              (city) => ({
                city: getDisplayCity(city.city),
                barCount: city.barCount,
                itemCount: city.itemCount,
              }),
            );

        const topBars = details
          ? Array.from(details.bars.entries())
              .map(([barId, entry]) => ({
                barId,
                barName: entry.barName,
                city: entry.city,
                itemCount: entry.itemCount,
                claimed: entry.claimed,
                lat: entry.lat,
                lon: entry.lon,
              }))
              .sort((left, right) => {
                if (right.itemCount !== left.itemCount) {
                  return right.itemCount - left.itemCount;
                }
                return left.barName.localeCompare(right.barName, "de");
              })
          : [];

        const resolvedBarCount = details?.bars.size ?? data.barCount ?? 0;
        const resolvedClaimedBarCount = details
          ? Array.from(details.bars.values()).filter((entry) => entry.claimed).length
          : data.claimedBarCount ?? 0;
        const resolvedCityCount = details?.cities.size ?? data.cityCount ?? 0;

        const resolvedBrand = details?.brand ?? filterDemand?.brand ?? brand;

        return {
          id: analyticsEntry?.id ?? normalizedBrand,
          ...data,
          brand: resolvedBrand,
          // Suchbarer normalisierter Name (Key kann eine brandId sein)
          normalizedBrand: normalizeSearchValue(resolvedBrand) || normalizedBrand,
          itemCount: data.itemCount ?? 0,
          barCount: resolvedBarCount,
          claimedBarCount: resolvedClaimedBarCount,
          activeBarCount: data.activeBarCount ?? resolvedBarCount,
          cityCount: resolvedCityCount,
          categories: normalizedCategories,
          averagePrice: data.averagePrice ?? 0,
          minPrice: data.minPrice ?? null,
          maxPrice: data.maxPrice ?? null,
          viewsThisMonth: data.viewsThisMonth ?? 0,
          menuViewsThisMonth: data.menuViewsThisMonth ?? 0,
          eventViewsThisMonth: data.eventViewsThisMonth ?? 0,
          offerViewsThisMonth: data.offerViewsThisMonth ?? 0,
          offerClaimsThisMonth: data.offerClaimsThisMonth ?? 0,
          callClicksThisMonth: data.callClicksThisMonth ?? 0,
          websiteClicksThisMonth: data.websiteClicksThisMonth ?? 0,
          directionClicksThisMonth: data.directionClicksThisMonth ?? 0,
          interactionCountThisMonth: data.interactionCountThisMonth ?? 0,
          conversionRateThisMonth: data.conversionRateThisMonth ?? 0,
          topCities,
          topBars,
          brandFilterApplyCountThisMonth: filterDemand?.applyCount ?? 0,
          brandFilterUniqueUsersThisMonth: filterDemand?.uniqueUsers.size ?? 0,
          brandFilterMultiSelectCountThisMonth: filterDemand?.multiSelectCount ?? 0,
          brandFilterShareOfTrackedAppliesThisMonth:
            trackedBrandFilterAppliesThisMonth > 0
              ? ((filterDemand?.applyCount ?? 0) / trackedBrandFilterAppliesThisMonth) * 100
              : 0,
        } as BrandAnalytics;
      })
      .filter((brand) => brand.brand.trim().length > 0)
      .sort((left, right) => left.brand.localeCompare(right.brand, "de"));
  },
};